import { css } from '@emotion/react'
import React, { memo, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, CircularProgress, Container, Grid, Snackbar, Typography } from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import ArrowForwardRoundedIcon from '@material-ui/icons/ArrowForwardRounded'
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded'
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined'
import AttachMoneyRoundedIcon from '@material-ui/icons/AttachMoneyRounded'
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined'
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined'
import CreditCardOutlinedIcon from '@material-ui/icons/CreditCardOutlined'
import PhoneIphoneOutlinedIcon from '@material-ui/icons/PhoneIphoneOutlined'
import CancelIcon from '@material-ui/icons/Cancel'
import ErrorRoundedIcon from '@material-ui/icons/ErrorRounded'
import { ModalInfo } from '@findep/mf-landings-core'
import { withThemeProps } from '@findep/microfronts-core'

import { getCookieValue } from '../../helpers/cookies'
import formatNumber from '../../helpers/formatNumber'
import { getPeriod } from '../../helpers/simulador'
import { navigate } from '../../helpers/queryNavigate'
import { CataloguesService, SimulatorService, StartService } from '../../services/LandingV4'
import useProductNavigate from '../../hooks/useProductNavigate'
import {
    restoreFromSessionStorageSrv,
    restoreFromSessionStorageTipo,
} from '../../components/pages/afi/restoreSessionStorage'
import HeroImage from '../../images/afi/family.png'
import plazosConfig from '../../data/components/plazos.json'

const catalogues = new CataloguesService()
let simulatorClient = null

const pageCss = css`
    background: #fafaf8;
    min-height: 100vh;
    padding: -5px 0 72px;
    margin-bottom: -72px;
    overflow-x: hidden;
`

const heroCss = css`
    position: relative;
    overflow: visible;
    min-height: 760px;
    border-radius: 0;
    padding: 21px 32px 0;
    background: #fafaf8;
    z-index: 1;

    @media (max-width: 959px) {
        min-height: auto;
        padding: 10px 18px 0;
        overflow-x: clip;
    }
`

const glowTopCss = css`
    position: absolute;
    top: -100px;
    right: -150px;
    width: 600px;
    height: 600px;
    border-radius: 300px;
    background: radial-gradient(50% 50% at 50% 50%, rgba(27, 107, 74, 0.07) 0%, rgba(27, 107, 74, 0) 70%);
    pointer-events: none;

    @media (max-width: 959px) {
        display: none;
    }
`

const glowBottomCss = css`
    position: absolute;
    left: 20%;
    bottom: -80px;
    width: 400px;
    height: 400px;
    border-radius: 200px;
    background: radial-gradient(50% 50% at 50% 50%, rgba(200, 151, 58, 0.06) 0%, rgba(200, 151, 58, 0) 70%);
    pointer-events: none;

    @media (max-width: 959px) {
        display: none;
    }
`

const badgeCss = css`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border: 1px solid rgba(255, 122, 69, 0.38);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.6);
    color: #ff7a45;
    font-size: 12px;
    line-height: 1.4;
`

const titleCss = css`
    margin-top: -20px;
    max-width: 771px;
    color: #1a1a1a;
    font-family: 'Jost-Bold', 'Jost', 'Helvetica Neue', Arial, sans-serif;
    font-size: 54.4px;
    line-height: 62.6px;
    font-weight: 700;
    letter-spacing: -0.5px;

    @media (max-width: 959px) {
        max-width: none;
        margin-top: 18px;
        font-size: 28px;
        line-height: 1.24;
        max-width: none;
    }
`

const accentCss = css`
    color: #ff7a45;
`

const subtitleCss = css`
    margin-top: 18px;
    max-width: 620px;
    color: #747b84;
    font-size: 16px;
    line-height: 1.7;

    @media (max-width: 959px) {
        margin-top: 12px;
        max-width: 320px;
        font-size: 13px;
        line-height: 1.55;
    }
`

const imageWrapCss = css`
    position: relative;
    margin-top: -10px;
    max-width: 980px;
    width: 128%;
    margin-bottom: -96px;
    z-index: 1;

    @media (max-width: 959px) {
        width: calc(100% + 36px);
        margin-left: -18px;
        margin-top: 18px;
        margin-bottom: -74px;
    }
`

const imageGlowLeftCss = css`
    position: absolute;
    left: 10px;
    top: 36px;
    width: 190px;
    height: calc(100% - 84px);
    border-radius: 120px 0 0 120px;
    background: linear-gradient(180deg, rgba(181, 240, 214, 0.9) 0%, rgba(181, 240, 214, 0.18) 100%);
`

const imageGlowRightCss = css`
    position: absolute;
    right: -20px;
    top: 0;
    width: 230px;
    height: 100%;
    border-radius: 160px;
    background: linear-gradient(180deg, rgba(212, 246, 228, 0.8) 0%, rgba(181, 240, 214, 0.35) 100%);
`

const imageCss = css`
    position: relative;
    z-index: 1;
    display: block;
    width: 100%;
    height: auto;
    border-radius: 140px 180px 160px 140px / 120px 120px 150px 120px;

    @media (max-width: 959px) {
        border-radius: 0;
    }
`

const cardCss = css`
    position: relative;
    z-index: 3;
    width: 100%;
    max-width: 388px;
    min-height: 516px;
    padding: 28px 20px 20px;
    border: 1px solid rgba(27, 107, 74, 0.08);
    border-radius: 22px;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 4px 40px rgba(0, 0, 0, 0.08);

    @media (max-width: 959px) {
        min-height: auto;
        max-width: none;
        padding: 22px 16px 18px;
        border-radius: 26px;
        box-shadow: 0 10px 35px rgba(32, 37, 41, 0.10);
    }
`

const cardTitleCss = css`
    color: #1a1a1a;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
`

const cardSubtitleCss = css`
    margin-top: 8px;
    color: #6b7280;
    font-family: 'DM Sans', 'Helvetica Neue', Arial, sans-serif;
    font-size: 13px;
    line-height: 1.6;

    @media (max-width: 959px) {
        font-size: 12px;
        line-height: 1.55;
    }
`

const labelCss = css`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
    color: #3d3d3d;
    font-size: 12px;
    font-weight: 500;

    @media (max-width: 959px) {
        min-height: 38px;
        align-items: flex-start;
    }
`

const labelDisabledCss = css`
    color: #d8d2cb;
`

const inputWrapCss = css`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 38px;
    padding: 0 12px;
    border: 1px solid #e2e5e9;
    border-radius: 10px;
    background: #ffffff;

    @media (max-width: 959px) {
        min-height: 52px;
        padding: 0 14px;
    }
`

const inputDisabledCss = css`
    border-color: #ebe7e1;
    background: #ffffff;
`

const inputCss = css`
    width: 100%;
    border: 0;
    outline: 0;
    background: transparent;
    color: #3d3d3d;
    font-size: 14px;
    line-height: 1.6;

    &::placeholder {
        color: #9ca3af;
    }

    &[type='number'] {
        appearance: textfield;
    }

    &[type='number']::-webkit-outer-spin-button,
    &[type='number']::-webkit-inner-spin-button {
        appearance: none;
        margin: 0;
    }
`

const inputErrorCss = css`
    border-color: #d14343;
`

const helperTextCss = css`
    margin-top: 6px;
    color: #d14343;
    font-size: 11px;
    line-height: 1.6;
`

const hintCss = css`
    margin-top: 6px;
    color: #6b7280;
    font-size: 11px;
    line-height: 1.7;
`

const hintDisabledCss = css`
    color: #e0d9d1;
`

const toggleWrapCss = css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
    padding: 4px;
    border-radius: 999px;
    background: #ece9e2;
`

const toggleWrapDisabledCss = css`
    background: #f5f2ed;
`

const toggleBtnCss = css`
    min-height: 34px;
    border: 1px solid transparent;
    border-radius: 999px;
    background: transparent;
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;

    @media (max-width: 959px) {
        min-height: 44px;
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`

const toggleBtnDisabledCss = css`
    color: #d8d2cb;
    border-color: transparent;
    background: transparent;
    box-shadow: none;
`

const toggleBtnActiveCss = css`
    border-color: #ff7a45;
    background: #ffffff;
    color: #ff7a45;
    box-shadow: 0 2px 8px rgba(27, 107, 74, 0.12);
`

const resultCss = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    margin-top: 18px;
    padding: 10px 20px;
    border: 1px solid rgba(255, 122, 69, 0.38);
    border-radius: 16px;
    background: rgba(232, 80, 10, 0.06);

    @media (max-width: 959px) {
        margin-top: 14px;
        min-height: 88px;
        justify-content: center;
    }
`

const resultDisabledCss = css`
    border-color: #ebe7e1;
    background: #ffffff;
`

const resultLabelCss = css`
    color: #6b7280;
    font-size: 13px;
    line-height: 1.6;
`

const resultLabelDisabledCss = css`
    color: #d8d2cb;
`

const resultValueCss = css`
    color: #ff7a45;
    font-size: 28px;
    line-height: 1;
    font-weight: 700;
`

const resultValueDisabledCss = css`
    color: #d8d2cb;
`

const ctaCss = css`
    width: 100%;
    margin-top: 16px;
    min-height: 46px;
    border: 1px solid #ff7a45;
    border-radius: 999px;
    background: transparent;
    color: #ff7a45;
    font-size: 15px;
    font-weight: 600;
    text-transform: none;

    @media (max-width: 959px) {
        min-height: 58px;
        margin-top: 18px;
        font-size: 16px;
    }

    &:hover {
        background: rgba(255, 122, 69, 0.06);
    }

    &:disabled {
        opacity: 0.45;
        color: #ff7a45;
    }
`

const ctaDisabledCss = css`
    border-color: #e7e2da;
    color: #d8d2cb;
    background: #ffffff;
`

const footerHintCss = css`
    margin-top: 10px;
    text-align: center;
    color: #6b7280;
    font-size: 11px;
    line-height: 1.6;

    @media (max-width: 959px) {
        margin-top: 14px;
        text-align: left;
        font-size: 11px;
        line-height: 1.9;
    }
`

const inlineAlertCss = css`
    display: grid;
    grid-template-columns: 28px 1fr;
    gap: 12px;
    align-items: center;
    margin-top: 18px;
    padding: 16px 18px;
    border: 1px solid #e05252;
    border-radius: 18px;
    background: #ffffff;
`

const inlineAlertIconCss = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 999px;
    background: #e05252;
    color: #ffffff;
`

const inlineAlertTextCss = css`
    color: #e05252;
    font-size: 14px;
    line-height: 1.4;
`

const benefitsSectionCss = css`
    margin-top: -36px;
    position: relative;
    z-index: 0;
    padding: 56px 32px 48px;
    border-radius: 0;
    background: #fafaf8;

    @media (max-width: 959px) {
        margin-top: -24px;
        padding: 40px 20px 28px;
    }
`

const benefitsInnerCss = css`
    position: relative;
    z-index: 2;
`

const benefitsEyebrowCss = css`
    color: #ff7a45;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
`

const benefitsTitleCss = css`
    margin-top: 10px;
    color: #1a1a1a;
    font-size: 40px;
    line-height: 1.2;
    font-weight: 700;

    @media (max-width: 959px) {
        font-size: 32px;
    }
`

const italicAccentCss = css`
    color: #ff7a45;
    font-style: italic;
`

const benefitsSubtitleCss = css`
    margin-top: 12px;
    max-width: 720px;
    color: #6b7280;
    font-size: 16px;
    line-height: 1.7;

    @media (max-width: 959px) {
        max-width: 320px;
        font-size: 13px;
        line-height: 1.65;
    }
`

const benefitCardCss = css`
    height: 100%;
    min-height: 192px;
    padding: 28px 26px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 20px;
    background: #ffffff;

    @media (max-width: 959px) {
        min-height: 262px;
        padding: 22px 22px 24px;
        border-radius: 24px;
    }
`

const benefitIconWrapCss = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: rgba(232, 80, 10, 0.06);
    color: #ff7a45;
`

const benefitTitleCss = css`
    margin-top: 18px;
    color: #1a1a1a;
    font-size: 18px;
    line-height: 1.5;
    font-weight: 600;

    @media (max-width: 959px) {
        font-size: 16px;
        line-height: 1.45;
    }
`

const benefitTextCss = css`
    margin-top: 12px;
    color: #6b7280;
    font-size: 14px;
    line-height: 1.75;

    @media (max-width: 959px) {
        font-size: 12px;
        line-height: 1.75;
    }
`

const benefitLinkCss = css`
    color: #e8500a;
`

const testimonialsSectionCss = css`
    padding: 12px 32px 32px;
    background: #fafaf8;

    @media (max-width: 959px) {
        padding: 8px 20px 24px;
    }
`

const testimonialsEyebrowCss = css`
    color: #ff7a45;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
`

const testimonialsTitleCss = css`
    margin-top: 10px;
    color: #1a1a1a;
    font-size: 40px;
    line-height: 1.2;
    font-weight: 700;

    @media (max-width: 959px) {
        font-size: 32px;
    }
`

const testimonialsSubtitleCss = css`
    margin-top: 12px;
    max-width: 690px;
    color: #6b7280;
    font-size: 16px;
    line-height: 1.7;

    @media (max-width: 959px) {
        max-width: 320px;
        font-size: 13px;
        line-height: 1.65;
    }
`

const testimonialCardCss = css`
    height: 100%;
    min-height: 312px;
    padding: 28px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 20px;
    background: #ffffff;

    @media (max-width: 959px) {
        min-height: 416px;
        padding: 28px 24px 26px;
        border-radius: 24px;
    }
`

const testimonialStarsCss = css`
    color: #e8611a;
    font-size: 16px;
    line-height: 1.6;
`

const testimonialQuoteCss = css`
    margin-top: 16px;
    color: #3d3d3d;
    font-size: 14.7px;
    line-height: 1.7;
    font-style: italic;

    @media (max-width: 959px) {
        font-size: 13px;
        line-height: 1.9;
    }
`

const testimonialAuthorCss = css`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 18px;
`

const testimonialAvatarCss = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 20px;
    color: #ffffff;
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;
`

const testimonialNameCss = css`
    color: #1a1a1a;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.5;
`

const testimonialLocationCss = css`
    color: #6b7280;
    font-size: 12.5px;
    line-height: 1.6;
`

const mobileScrollerCss = css`
    display: flex;
    gap: 16px;
    margin-top: 18px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    padding-bottom: 6px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`

const mobileSlideCss = css`
    min-width: 88%;
    scroll-snap-align: start;
`

const mobileDotsCss = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    margin-top: 18px;
`

const mobileDotCss = css`
    width: 12px;
    height: 12px;
    border-radius: 999px;
    background: #ffffff;
    opacity: 0.95;
`

const mobileDotActiveCss = css`
    background: #a6a6a6;
`

const testimonialsEs = [
    {
        stars: '★★★★★',
        quote: '"Es una excelente empresa que me dio la oportunidad de cumplir uno de mis sueños al mejorar el modo de vida de mi familia que no está en este país."',
        initials: 'AG',
        name: 'América Galo',
        location: 'San José',
        avatarBg: '#ff7a45',
    },
    {
        stars: '★★★★★',
        quote: '"Soy cliente de Apoyo Financiero desde hace 16 años. El primer préstamo lo obtuve cuando estaba en San Fco y lo utilicé para pagar las deudas que tenía en México. Gracias al personal."',
        initials: 'FM',
        name: 'Faustino Mota',
        location: 'Oakland',
        avatarBg: '#e8611a',
    },
    {
        stars: '★★★★★',
        quote: '"Me ayudaron cuando más lo necesitaba. La app es muy fácil de usar y puedo hacer mis pagos desde casa. Ya llevo dos préstamos con ellos y siempre excelente servicio."',
        initials: 'PP',
        name: 'Paulino Pat Estrella',
        location: 'San Francisco',
        avatarBg: '#2a8f63',
    },
    {
        stars: '★★★★★',
        quote: '"Soy cliente de Apoyo Financiero desde el 2022 y solicité un préstamo para mejorar mi historial de crédito. En la sucursal han sido siempre muy solidarios y amables. El trámite fue rápido y muy confiable."',
        initials: 'JH',
        name: 'Jose Herrera',
        location: 'Tracy',
        avatarBg: '#7b5ea7',
    },
]

const testimonialsEn = [
    {
        stars: '★★★★★',
        quote: '"It is an excellent company that gave me the opportunity to realize my dreams which was to better the life of my family not in this country."',
        initials: 'AG',
        name: 'América Galo',
        location: 'San José',
        avatarBg: '#ff7a45',
    },
    {
        stars: '★★★★★',
        quote: '"I have been an Apoyo Financiero client for sixteen years. I obtained my first loan when I lived in San Francisco and used it to pay debts I had in Mexico. I send my thanks to the team at Apoyo Financiero."',
        initials: 'FM',
        name: 'Faustino Mota',
        location: 'Oakland',
        avatarBg: '#e8611a',
    },
    {
        stars: '★★★★★',
        quote: '"One of the greatest satisfactions Apoyo Financiero gave me was to realize part of my dreams when I came to this country."',
        initials: 'PP',
        name: 'Paulino Pat Estrella',
        location: 'San Francisco',
        avatarBg: '#2a8f63',
    },
    {
        stars: '★★★★★',
        quote: '"I have been a customer of Apoyo Financiero since 2022. I requested a loan to improve my credit history. At the branch they have always been very supportive and kind. The process was fast and very reliable."',
        initials: 'JH',
        name: 'Jose Herrera',
        location: 'Tracy',
        avatarBg: '#7b5ea7',
    },
]

function getEstado(estado) {
    switch (estado) {
        case 'AZ':
            return 'Arizona'
        case 'TX':
            return 'Texas'
        case 'CA':
        default:
            return 'California'
    }
}

function getEstadoShort(estado) {
    switch ((estado || '').toUpperCase()) {
        case 'ARIZONA':
            return 'AZ'
        case 'TEXAS':
            return 'TX'
        case 'CALIFORNIA':
        default:
            return 'CA'
    }
}

function getPaymentFrequency(option) {
    const period = `${option?.extra?.periodo || option?.period || ''}`.toLowerCase()
    const label = `${option?.label || option?.value || ''}`.toLowerCase()

    if (period.includes('cator') || period.includes('quinc') || period.includes('semi') || label.includes('cator') || label.includes('quinc')) {
        return 'Catorcenal'
    }

    return 'Mensual'
}

function getDisplayMonths(option) {
    const label = `${option?.label || option?.value || ''}`
    const match = label.match(/(\d+)\s*mes/i)
    return match ? match[1] : label
}

function normalizePaymentOptions(paymentMethod) {
    return (paymentMethod || []).map((option, index) => ({
        key: `${option?.value || option?.label || 'option'}-${index}`,
        raw: option,
        label: option?.label || option?.value || '',
        displayMonths: getDisplayMonths(option),
        frequency: getPaymentFrequency(option),
        baseTerms: Number(option?.extra?.periodicidad || option?.terms || 0),
        basePeriod: option?.extra?.periodo || option?.period || '',
    }))
}

function getDisplayLabel(option, frequency) {
    return (option?.label || option?.value || '').trim()
}

function getPeriodFromLabel(label) {
    const normalizedLabel = `${label || ''}`.toLowerCase()

    if (normalizedLabel.includes('mens') || normalizedLabel.includes('month')) {
        return 'mensual'
    }

    return 'catorcenal'
}

function getTermsFromLabel(label) {
    const match = `${label || ''}`.match(/(\d+)\s+(?:catorcenal|mensual|biweekly|monthly)/i)
    return match ? Number(match[1]) : 0
}

function getBasePaymentOptions(productId, locale) {
    const productConfig = plazosConfig?.plazos?.find(
        (item) => item.company === 'AFI' && item.producto === String(productId) && item.lang === locale
    )

    if (!productConfig) return []

    const defaultSource = productConfig.arrayDefault?.[0]
    const options = productConfig[defaultSource] || []

    return options.map((option) => ({
        ...option,
        extra: {
            periodicidad: getTermsFromLabel(option.label),
            periodo: getPeriodFromLabel(option.label),
        },
    }))
}

function getTermsAndPeriod(option, frequency) {
    const months = Number.parseInt(option?.displayMonths, 10)

    if (frequency === 'Mensual') {
        return {
            terms: months,
            period: 'mensual',
        }
    }

    return {
        terms: option?.baseTerms || Math.round((months * 26) / 12),
        period: option?.basePeriod || 'catorcenal',
    }
}

function clampAmount(value, min, max) {
    if (!value) return ''
    const parsed = Number.parseInt(value, 10)
    if (Number.isNaN(parsed)) return ''
    return String(Math.min(Math.max(parsed, min), max))
}

function readJsonStorage(storage, key, fallback = null) {
    if (typeof window === 'undefined' || !storage) return fallback

    try {
        const raw = storage.getItem(key)
        return raw ? JSON.parse(raw) : fallback
    } catch (error) {
        return fallback
    }
}

function Simulador({
    pageContext,
    heroBadge,
    heroTitlePrefix,
    heroTitleAccent,
    heroTitleSuffix,
    heroSubtitleText,
    heroImageAlt,
    simulatorCardTitle,
    simulatorCardSubtitle,
    postalCodeLabel,
    postalCodePlaceholder,
    postalCodeErrorText,
    loanAmountLabel,
    amountRangeMinLabel,
    amountRangeMaxLabel,
    coverageUnavailableText,
    paymentFrequencyLabel,
    monthlyLabel,
    biweeklyLabel,
    termLabel,
    estimatedPaymentMonthlyLabel,
    estimatedPaymentBiweeklyLabel,
    continueButtonText,
    simulatorFooterHint,
    benefitsEyebrow,
    benefitsTitlePrefix,
    benefitsTitleAccent,
    benefitsSubtitleText,
    benefitOneTitle,
    benefitOneLine1,
    benefitOneLine2,
    benefitOneLine3,
    benefitTwoTitle,
    benefitTwoLine1,
    benefitTwoLine2,
    benefitTwoLine3,
    benefitThreeTitle,
    benefitThreeLine1,
    benefitThreeLine2,
    benefitThreeLine3,
    benefitThreeLinkText,
    testimonialsEyebrowText,
    testimonialsTitlePrefix,
    testimonialsTitleAccent,
    testimonialsSubtitleText,
    alertServiceUnavailable,
    inactiveBusinessAlert,
    modalTitleSorry,
    modalBodyAge,
}) {
    const productNavigate = useProductNavigate()
    const benefitsScrollerRef = useRef(null)
    const testimonialsScrollerRef = useRef(null)
    const [cp, setCp] = useState('')
    const [cpError, setCpError] = useState('')
    const [dedicacion, setDedicacion] = useState('')
    const [monto, setMonto] = useState('')
    const [plazo, setPlazo] = useState('36')
    const [selectedPlazoKey, setSelectedPlazoKey] = useState('')
    const [frecuencia, setFrecuencia] = useState('Catorcenal')
    const [period, setPeriod] = useState('catorcenal')
    const [terms, setTerms] = useState(78)
    const [pago, setPago] = useState('')
    const [paymentMethod, setPaymentMethod] = useState([])
    const [flujo, setFlujo] = useState(() =>
        readJsonStorage(typeof window !== 'undefined' ? window.sessionStorage : undefined, 'flujo', {})
    )
    const [showSimulator, setShowSimulator] = useState(false)
    const [errorService, setErrorService] = useState(false)
    const [msjAlert, setMsjAlert] = useState('')
    const [minVar, setMinVar] = useState(500)
    const [maxVar, setMaxVar] = useState(10000)
    const [defaultAmountVar, setDefaultAmountVar] = useState(4000)
    const [debouncedMonto, setDebouncedMonto] = useState('')
    const [coverageUnavailable, setCoverageUnavailable] = useState(false)
    const [openProductModal, setOpenProductModal] = useState(false)
    const [loadingQuote, setLoadingQuote] = useState(false)
    const [activeBenefitSlide, setActiveBenefitSlide] = useState(0)
    const [activeTestimonialSlide, setActiveTestimonialSlide] = useState(0)
    const locale = pageContext.locale === 'enUS' ? 'en' : 'es'
    const testimonials = locale === 'en' ? testimonialsEn : testimonialsEs
    const normalizedPaymentOptions = useMemo(() => normalizePaymentOptions(paymentMethod), [paymentMethod])
    const filteredPaymentOptions = normalizedPaymentOptions

    const updateFlujo = (updater) => {
        setFlujo((current) => {
            const next = typeof updater === 'function' ? updater(current || {}) : updater
            if (typeof window !== 'undefined') {
                sessionStorage.setItem('flujo', JSON.stringify(next))
            }
            return next
        })
    }

    const handlePaymentOptionChange = (nextKey) => {
        const nextOption = normalizedPaymentOptions.find((option) => option.key === nextKey)
        if (!nextOption) return
        const nextTermsAndPeriod = getTermsAndPeriod(nextOption, frecuencia)

        setSelectedPlazoKey(nextOption.key)
        setPlazo(nextOption.displayMonths)
        setTerms(nextTermsAndPeriod.terms)
        setPeriod(nextTermsAndPeriod.period)
    }

    const formatDate = (date) => {
        const dd = String(date.getDate()).padStart(2, '0')
        const mm = String(date.getMonth() + 1).padStart(2, '0')
        const yyyy = date.getFullYear()
        const hh = String(date.getHours()).padStart(2, '0')
        const min = String(date.getMinutes()).padStart(2, '0')
        const ss = String(date.getSeconds()).padStart(2, '0')
        return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`
    }

    const registerLoanCenter = async (tipo, creditAppId = null, userId = null) => {
        const creditApplicationId = creditAppId || flujo?.creditApplicationId || ''
        const usuarioId = userId || ' '

        try {
            await catalogues.funnelErrors({
                creditApplicationId,
                tipo,
                nombreCompleto: '',
                clienteId: '',
                sucursalId: '',
                campania: '',
                usuarioId,
                dateCreated: formatDate(new Date()),
            })
        } catch (error) {
            console.error(`Error registering loan center ${tipo}:`, error)
        }
    }

    const initCreditAplication = async () => {
        let currentFlujo = readJsonStorage(sessionStorage, 'flujo', {})
        const originState = readJsonStorage(sessionStorage, 'afiStateShort', '')
        const urlParams = new URLSearchParams(window.location.search)
        let broker = ''
        const tenant = pageContext.tenant || 'AEF'
        let origen

        const fbp = getCookieValue('_fbp')
        const fbc = getCookieValue('_fbc')
        const channel = getCookieValue('channel') || 'canary'

        if (urlParams.has('origen') || urlParams.has('broker')) {
            origen = urlParams.get('origen') || urlParams.get('broker')
            broker = origen
        }

        const referredById = urlParams.get('referredById') || ''
        const source = urlParams.get('source') || urlParams.get('utm_source') || 'search_engine'
        const medium = urlParams.get('medium') || urlParams.get('utm_medium') || 'search'
        const campaign = urlParams.get('campaign') || urlParams.get('utm_campaign') || 'seo'
        let term = urlParams.get('term') || urlParams.get('utm_term') || 'idmedio_935'
        const content = urlParams.get('content') || urlParams.get('utm_content') || 'redirect_sitioafi'
        const operatorEmail = urlParams.get('operatorEmail')
        const gclid = urlParams.get('gclid')
        const fbclid = urlParams.get('fbclid')
        const version = `1-${channel}`
        let sellerPartner = urlParams.get('sellerPartner') || ''
        const sellerId = urlParams.get('sellerId') || ''
        let branchId = urlParams.get('branchId') || 0

        if (sellerPartner !== '') {
            term = term === 'idmedio_935' ? 'idmedio_1391' : term
            try {
                const res = await catalogues.referidosCheck(sellerPartner)
                if (res.data?.data?.business.estatus === 'C') {
                    setErrorService(true)
                    setMsjAlert(inactiveBusinessAlert || 'Lo sentimos, el establecimiento esta desactivado, se continuara con una solicitud normal')
                    sellerPartner = ''
                    branchId = 0
                }
            } catch (error) {
                console.error(error)
                setErrorService(true)
            }
        }

        if (!currentFlujo?.simulador || !currentFlujo.creditApplicationId) {
            const start = new StartService(tenant, broker || pageContext.broker, originState, referredById, version)
            start.setUtmSource(source)
            start.setUtmMedium(medium)
            start.setUtmCampaign(campaign)
            start.setUtmTerm(term)
            start.setOperatorEmail(operatorEmail)
            start.setUtmContent(content)
            start.setOriginState(originState)
            start.setBrowserId(fbclid || fbp)
            start.setClickId(gclid || fbc)
            start.setSellerPartner(sellerPartner)
            start.setSellerId(sellerId)
            start.setBranchId(Number(branchId))

            try {
                const { data: startData } = await start.send()
                currentFlujo = {
                    ...currentFlujo,
                    creditApplicationId: startData.id,
                    simulador: {
                        availableProducts: startData.availableProducts,
                    },
                }

                if (startData.forceRedirect !== null && startData.forceRedirect !== undefined) {
                    navigate(startData.forceRedirect)
                }

                simulatorClient = new SimulatorService(currentFlujo.creditApplicationId)
                updateFlujo(currentFlujo)
                setDedicacion(startData.availableProducts?.[0]?.id || '')

                try {
                    await catalogues.putChangueLang(startData.id, locale)
                } catch (error) {
                    console.error(error)
                }

                // Registrar carga de calculadora en Loan Center
                registerLoanCenter('orig_carga_calculadora', startData.id, operatorEmail || '')
            } catch (error) {
                console.error(error)
                setErrorService(true)
            }
        } else {
            try {
                const response = await catalogues.callProducts(broker, originState)
                currentFlujo = {
                    ...currentFlujo,
                    simulador: {
                        availableProducts: response.data.availableProducts,
                    },
                }

                simulatorClient = new SimulatorService(currentFlujo.creditApplicationId)
                updateFlujo(currentFlujo)
                setDedicacion(response.data.availableProducts?.[0]?.id || '')

                // Registrar carga de calculadora en Loan Center
                registerLoanCenter('orig_carga_calculadora', currentFlujo.creditApplicationId, operatorEmail || '')
            } catch (error) {
                console.error(error)
                setErrorService(true)
            }
        }
    }

    const validateStorage = async (neighborhoods) => {
        const stateOriginSh = getEstadoShort(neighborhoods?.[0]?.state)
        const stateOrigin = getEstado(stateOriginSh)

        sessionStorage.setItem('afiState', JSON.stringify(stateOrigin))
        sessionStorage.setItem('afiStateShort', JSON.stringify(stateOriginSh))

        await initCreditAplication()
    }

    const resolvePostalCode = async (postalCode) => {
        const sanitizedCode = `${postalCode || ''}`.replace(/\D/g, '').slice(0, 5)

        if (sanitizedCode.length < 5) {
            setCp(sanitizedCode)
            setCpError(sanitizedCode.length === 0 ? '' : (postalCodeErrorText || 'Se aceptan solo 5 dígitos'))
            return
        }

        setCp(sanitizedCode)
        setCpError('')
        localStorage.setItem('cp', sanitizedCode)

        try {
            const response = await catalogues.getNeighborhoodsAfi(sanitizedCode)
            const neighborhoods = [...(response.data?.neighborhoods || [])]

            sessionStorage.setItem('codepostal', sanitizedCode)
            updateFlujo((current) => ({
                ...(current || {}),
                covered: response.data.coverageResult,
                sucursalId: neighborhoods?.[0]?.sucursalId,
            }))

            if (response.data.coverageResult === 'Covered' || response.data.coverageResult === 'CoveredNoLocalBranch') {
                setCoverageUnavailable(false)
                await validateStorage(neighborhoods)
                setShowSimulator(true)
                return
            }

            setShowSimulator(false)
            setCoverageUnavailable(true)
            setSelectedPlazoKey('')
            setPlazo('')
            setTerms('')
            setPeriod('')
            setPago('')
        } catch (error) {
            console.error(error)
            setErrorService(true)
        }
    }

    const initVariablesLP = async () => {
        const urlParams = new URLSearchParams(window.location.search)
        const term = urlParams.get('term') || urlParams.get('utm_term')
        const medioId = term !== null ? term.substring('idmedio_'.length) : '935'
        const srvConnect = restoreFromSessionStorageSrv('srvConnect')
        const afiSShort = urlParams.get('afiStateShort') || 'NotFound'

        if (srvConnect === 'notFound') {
            try {
                const response = await catalogues.getMediosFunnel(medioId)
                if (response.data.estado !== '') {
                    const edoAfi = afiSShort === 'NotFound' ? response.data.estado : afiSShort
                    sessionStorage.setItem('afiState', JSON.stringify(getEstado(edoAfi)))
                    sessionStorage.setItem('afiStateShort', JSON.stringify(edoAfi))
                    sessionStorage.setItem('tipoLP', JSON.stringify(response.data.tipoLP))
                    sessionStorage.setItem('srvConnect', JSON.stringify('connect'))
                }
            } catch (error) {
                console.error(error)
            }
        }
    }

    const runPaymentQuote = async (productId, amount, nextTerms, nextPeriod) => {
        const stateOrigin = readJsonStorage(sessionStorage, 'afiStateShort', '')
        const parsedAmount = Number.parseInt(amount, 10)
        let product = productId

        if (stateOrigin === 'CA' && parsedAmount <= 2499) {
            product = 4
        }

        if (!simulatorClient && flujo?.creditApplicationId) {
            simulatorClient = new SimulatorService(flujo.creditApplicationId)
        }

        if (!simulatorClient || Number.isNaN(parsedAmount)) return

        setLoadingQuote(true)
        simulatorClient.setProductId(product)
        simulatorClient.setRequestedAmount(parsedAmount)
        simulatorClient.setTerms(Number(nextTerms))
        simulatorClient.setLang(locale)
        simulatorClient.setPeriod(getPeriod(nextPeriod))
        simulatorClient.setOriginState(stateOrigin)

        try {
            const { data: dataSimulator } = await simulatorClient.send()
            sessionStorage.setItem(
                'cotizador',
                JSON.stringify({
                    renta: dataSimulator.estimatedPayment,
                    cat: dataSimulator.cat,
                    monto: parsedAmount,
                })
            )
            setPago(dataSimulator.estimatedPayment)
            setPaymentMethod(dataSimulator.arrayQuincenas || [])
        } catch (error) {
            console.error(error)
            setErrorService(true)
        } finally {
            setLoadingQuote(false)
        }
    }

    useEffect(() => {
        if (typeof window === 'undefined') return undefined

        let active = true

        const boot = async () => {
            await initVariablesLP()

            if (!active) return

            const tipoLp = restoreFromSessionStorageTipo('tipoLP')
            const postalCode = sessionStorage.getItem('codepostal') || 'notFound'

            if (postalCode === 'notFound') {
                const recomienda = readJsonStorage(sessionStorage, 'recomendados', {})
                if (recomienda?.prospectoId) {
                    setCp(recomienda.postalCode || '')
                    await resolvePostalCode(recomienda.postalCode)
                    return
                }
            }

            if (tipoLp === 'PAGADA') {
                setShowSimulator(true)
                await initCreditAplication()
                return
            }

            if (postalCode !== 'notFound') {
                setCp(postalCode)
                setShowSimulator(true)
                await initCreditAplication()
            }
        }

        boot()

        return () => {
            active = false
        }
    }, [])

    useEffect(() => {
        const selectedProduct = flujo?.simulador?.availableProducts?.find((product) => product.id === dedicacion)

        if (!selectedProduct) return

        if (selectedProduct.max < 1 || selectedProduct.min < 1) {
            setMinVar(selectedProduct.min || 500)
            setMaxVar(selectedProduct.min || 500)
            setDefaultAmountVar(selectedProduct.min || 500)
            setOpenProductModal(true)
            return
        }

        const fallbackAmount =
            selectedProduct.defaultAmount >= selectedProduct.min && selectedProduct.defaultAmount <= selectedProduct.max
                ? selectedProduct.defaultAmount
                : selectedProduct.min

        const basePaymentOptions = getBasePaymentOptions(selectedProduct.id, locale)

        setMinVar(selectedProduct.min)
        setMaxVar(selectedProduct.max)
        setDefaultAmountVar(fallbackAmount)
        setPaymentMethod(basePaymentOptions)

        setMonto((current) => {
            const clamped = clampAmount(current, selectedProduct.min, selectedProduct.max)
            return clamped || String(fallbackAmount)
        })
        setDebouncedMonto(String(fallbackAmount))

        if (!(selectedProduct.defaultAmount >= selectedProduct.min && selectedProduct.defaultAmount <= selectedProduct.max)) {
            setOpenProductModal(true)
        }
    }, [dedicacion, flujo])

    useEffect(() => {
        if (!filteredPaymentOptions.length) {
            setSelectedPlazoKey('')
            setPlazo('')
            setTerms('')
            setPeriod('')
            return
        }

        const currentOption = filteredPaymentOptions.find((option) => option.key === selectedPlazoKey)
        if (currentOption) return

        const preferredOption =
            filteredPaymentOptions.find((option) => option.displayMonths === '36') || filteredPaymentOptions[0]

        handlePaymentOptionChange(preferredOption.key)
    }, [filteredPaymentOptions, selectedPlazoKey, frecuencia])

    useEffect(() => {
        if (!selectedPlazoKey) return
        const currentOption = normalizedPaymentOptions.find((option) => option.key === selectedPlazoKey)
        if (!currentOption) return

        const nextTermsAndPeriod = getTermsAndPeriod(currentOption, frecuencia)

        // Solo actualizar si los valores realmente cambian para evitar llamadas duplicadas
        if (terms !== nextTermsAndPeriod.terms || period !== nextTermsAndPeriod.period) {
            setTerms(nextTermsAndPeriod.terms)
            setPeriod(nextTermsAndPeriod.period)
        }
    }, [frecuencia, selectedPlazoKey, normalizedPaymentOptions, terms, period])

    useEffect(() => {
        if (!showSimulator) return undefined

        const normalizedAmount = `${monto || ''}`.trim()

        if (!normalizedAmount) {
            setDebouncedMonto('')
            return undefined
        }

        const timeout = window.setTimeout(() => {
            setDebouncedMonto(normalizedAmount)
        }, 1000)

        return () => window.clearTimeout(timeout)
    }, [monto, showSimulator])

    useEffect(() => {
        if (!dedicacion || !debouncedMonto || !terms || !period || !showSimulator) return undefined

        const parsedDebouncedMonto = Number.parseInt(debouncedMonto, 10)
        if (Number.isNaN(parsedDebouncedMonto) || parsedDebouncedMonto < minVar || parsedDebouncedMonto > maxVar) {
            return undefined
        }

        const timeout = window.setTimeout(() => {
            runPaymentQuote(dedicacion, debouncedMonto, terms, period)
        }, 250)

        return () => window.clearTimeout(timeout)
    }, [dedicacion, debouncedMonto, terms, period, showSimulator, minVar, maxVar])

    const handlePostalCodeInput = (event) => {
        const nextValue = event.target.value.replace(/\D/g, '').slice(0, 5)
        setCp(nextValue)

        if (nextValue.length < 5) {
            setCpError(nextValue.length === 0 ? '' : (postalCodeErrorText || 'Se aceptan solo 5 dígitos'))
            setShowSimulator(false)
            setCoverageUnavailable(false)
            setSelectedPlazoKey('')
            setPlazo('')
            setTerms('')
            setPeriod('')
            setPago('')
            setDebouncedMonto('')
            setPaymentMethod([])
            return
        }

        resolvePostalCode(nextValue)
    }

    const handleMontoChange = (event) => {
        const nextValue = event.target.value.replace(/\D/g, '')
        if (!nextValue) {
            setMonto('')
            return
        }

        const parsedValue = Number.parseInt(nextValue, 10)
        if (parsedValue > 4000) {
            return
        }

        setMonto(nextValue)
    }

    const handleMontoBlur = () => {
        const nextAmount = clampAmount(monto, minVar, maxVar) || String(defaultAmountVar)
        setMonto(nextAmount)
        setDebouncedMonto(nextAmount)

        if (dedicacion && terms && period && showSimulator) {
            runPaymentQuote(dedicacion, nextAmount, terms, period)
        }
    }

    const handleContinue = () => {
        const urlParams = new URLSearchParams(window.location.search)
        const operatorEmail = urlParams.get('operatorEmail') || ''

        registerLoanCenter('orig_click_continuar_calculadora', null, operatorEmail)

        productNavigate({
            company: pageContext.company,
            product: dedicacion,
            journey: pageContext.journey,
            fallbackUrl: pageContext?.next?.path,
            navigate,
        })
    }

    const updateActiveSlide = (node, setActiveSlide, totalItems) => {
        if (!node || !node.firstElementChild || !totalItems) return

        const styles = window.getComputedStyle(node)
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0
        const itemWidth = node.firstElementChild.getBoundingClientRect().width + gap
        if (!itemWidth) return

        const nextIndex = Math.min(totalItems - 1, Math.max(0, Math.round(node.scrollLeft / itemWidth)))
        setActiveSlide(nextIndex)
    }

    const resultLabel =
        frecuencia === 'Catorcenal'
            ? (estimatedPaymentBiweeklyLabel || 'Pago estimado catorcenal')
            : (estimatedPaymentMonthlyLabel || 'Pago estimado mensual')
    const pagoFormateado = pago         ? formatNumber(pago, 3, ',', '$', 2, '.')        : !cp          ? '$0.00'          : '$0.00'
    const isCardReady = Boolean(showSimulator && dedicacion)
    const showBlockedCoverageState = coverageUnavailable && !isCardReady
    const showCoverageError = coverageUnavailable && !isCardReady

    return (
        <div css={pageCss}>
            <Container maxWidth="lg">
                <Snackbar open={errorService} autoHideDuration={6000} onClose={() => setErrorService(false)}>
                    <Alert variant="filled" severity="warning" onClose={() => setErrorService(false)}>
                        {msjAlert || alertServiceUnavailable || 'Lo sentimos, por el momento el servicio no se encuentra disponible'}
                    </Alert>
                </Snackbar>

                <section css={heroCss}>
                    <div css={glowTopCss} />
                    <div css={glowBottomCss} />

                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={7}>
                            

                            <Typography component="h1" css={titleCss}>
                                {heroTitlePrefix || 'El préstamo que necesitas, '}{' '}
                                <span css={accentCss}>{heroTitleAccent || 'fácil, rápido'}</span>{' '}
                                {heroTitleSuffix || 'y seguro.'}
                            </Typography>

                            <Typography component="p" css={subtitleCss}>
                                {heroSubtitleText || 'Nuestras soluciones inteligentes ayudan a fortalecer tus finanzas y a darte confianza.'}
                            </Typography>

                            <div css={imageWrapCss}>
                                <div css={imageGlowLeftCss} />
                                <div css={imageGlowRightCss} />
                                <img src={HeroImage} alt={heroImageAlt || 'Pareja revisando información en una tableta'} css={imageCss} />
                            </div>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <Box
                                css={css`
                                    position: relative;
                                    z-index: 3;
                                    margin-top: 12px;

                                    @media (min-width: 960px) {
                                        margin-top: 8px;
                                        margin-left: 90px;
                                    }

                                    @media (max-width: 959px) {
                                        margin-top: -22px;
                                        padding: 0 2px;
                                    }
                                `}
                            >
                                <Box css={[cardCss, !showSimulator && css`min-height: auto;`]}>
                                    <Typography component="h2" css={cardTitleCss}>
                                        {simulatorCardTitle || 'Calcula tu préstamo'}
                                    </Typography>

                                    <Typography component="p" css={cardSubtitleCss}>
                                        {simulatorCardSubtitle || 'Ingresa los valores y conoce tu pago estimado al instante.'}
                                    </Typography>

                                    <Grid container spacing={2} style={{ marginTop: 10 }}>
                                        <Grid item xs={showSimulator ? 6 : 12} sm={showSimulator ? 6 : 12}>
                                            <div css={labelCss}>
                                                <RoomOutlinedIcon style={{ fontSize: 14 }} />
                                                {postalCodeLabel || 'Código postal'}
                                            </div>
                                            <div css={[inputWrapCss, cpError && inputErrorCss]}>
                                                <input
                                                    id="simulador-afi-codigo-postal"
                                                    type="text"
                                                    value={cp}
                                                    onChange={handlePostalCodeInput}
                                                    onBlur={() => setCpError(cp && cp.length < 5 ? (postalCodeErrorText || 'Se aceptan solo 5 dígitos') : '')}
                                                    placeholder={postalCodePlaceholder || 'Ej. 94520'}
                                                    inputMode="numeric"
                                                    css={inputCss}
                                                />
                                            </div>
                                            {cpError ? <div css={helperTextCss}>{cpError}</div> : null}
                                        </Grid>

                                        {showSimulator && (
                                            <Grid item xs={6} sm={6}>
                                                <div css={[labelCss, showBlockedCoverageState && labelDisabledCss]}>
                                                    <AttachMoneyRoundedIcon style={{ fontSize: 14 }} />
                                                    {loanAmountLabel || 'Monto del préstamo'}
                                                </div>
                                                <div css={[inputWrapCss, showBlockedCoverageState && inputDisabledCss]}>
                                                    <span
                                                        style={{
                                                            color: showBlockedCoverageState ? '#d8d2cb' : '#6b7280',
                                                            marginRight: 8,
                                                            flexShrink: 0,
                                                            alignSelf: 'center',
                                                        }}
                                                    >
                                                        $
                                                    </span>
                                                    <input
                                                        id="simulador-afi-monto"
                                                        type="number"
                                                        value={monto}
                                                        onChange={handleMontoChange}
                                                        onBlur={handleMontoBlur}
                                                        placeholder="Ej. 4,000"
                                                        min={minVar}
                                                        max={maxVar}
                                                        disabled={!isCardReady}
                                                        css={[
                                                            inputCss,
                                                            showBlockedCoverageState &&
                                                                css`
                                                                    color: #d8d2cb;

                                                                    &::placeholder {
                                                                        color: #d8d2cb;
                                                                    }
                                                                `,
                                                        ]}
                                                    />
                                                </div>
                                                <div css={[hintCss, showBlockedCoverageState && hintDisabledCss]}>
                                                    {amountRangeMinLabel || 'Mínimo'} {formatNumber(minVar, 3, ',', '$', 0, '')} — {amountRangeMaxLabel || 'Máximo'}{' '}
                                                    {formatNumber(maxVar, 3, ',', '$', 0, '')}
                                                </div>
                                            </Grid>
                                        )}

                                        {showCoverageError ? (
                                            <Grid item xs={12}>
                                                <div css={inlineAlertCss}>
                                                    <span css={inlineAlertIconCss}>
                                                        <ErrorRoundedIcon style={{ fontSize: 18 }} />
                                                    </span>
                                                    <div css={inlineAlertTextCss}>
                                                        {coverageUnavailableText || 'Lo sentimos, actualmente no tenemos disponibilidad en esa zona.'}
                                                    </div>
                                                </div>
                                            </Grid>
                                        ) : null}

                                        {showSimulator && (
                                            <Grid item xs={12}>
                                                <div css={[labelCss, showBlockedCoverageState && labelDisabledCss]}>{paymentFrequencyLabel || 'Frecuencia de pago'}</div>
                                                <div css={[toggleWrapCss, showBlockedCoverageState && toggleWrapDisabledCss]}>
                                                    <button
                                                        id="simulador-afi-frecuencia-mensual"
                                                        type="button"
                                                        css={[
                                                            toggleBtnCss,
                                                            frecuencia === 'Mensual' && toggleBtnActiveCss,
                                                            showBlockedCoverageState && toggleBtnDisabledCss,
                                                        ]}
                                                        onClick={() => setFrecuencia('Mensual')}
                                                        disabled={!isCardReady}
                                                    >
                                                        {monthlyLabel || 'Mensual'}
                                                    </button>
                                                    <button
                                                        id="simulador-afi-frecuencia-catorcenal"
                                                        type="button"
                                                        css={[
                                                            toggleBtnCss,
                                                            frecuencia === 'Catorcenal' && toggleBtnActiveCss,
                                                            showBlockedCoverageState && toggleBtnDisabledCss,
                                                        ]}
                                                        onClick={() => setFrecuencia('Catorcenal')}
                                                        disabled={!isCardReady}
                                                    >
                                                        {biweeklyLabel || 'Catorcenal'}
                                                    </button>
                                                </div>
                                            </Grid>
                                        )}

                                        {showSimulator && (
                                            <Grid item xs={12}>
                                                <div css={[labelCss, showBlockedCoverageState && labelDisabledCss]}>
                                                    <CalendarTodayOutlinedIcon style={{ fontSize: 14 }} />
                                                    {termLabel || 'Plazo (meses)'}
                                                </div>
                                                <div css={[inputWrapCss, showBlockedCoverageState && inputDisabledCss]}>
                                                    <select
                                                        id="simulador-afi-plazo"
                                                        value={selectedPlazoKey}
                                                        onChange={(event) => handlePaymentOptionChange(event.target.value)}
                                                        disabled={!isCardReady || filteredPaymentOptions.length === 0}
                                                        css={[inputCss, css`appearance: none;`]}
                                                    >
                                                        {!selectedPlazoKey && !isCardReady ? (
                                                            <option value=""></option>
                                                        ) : null}
                                                        {filteredPaymentOptions.map((option) => (
                                                            <option key={option.key} value={option.key}>
                                                                {getDisplayLabel(option, frecuencia)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <KeyboardArrowDownRoundedIcon style={{ color: '#6b7280' }} />
                                                </div>
                                            </Grid>
                                        )}
                                    </Grid>

                                    {showSimulator && (loadingQuote || pago) && (
                                        <div css={[resultCss, showBlockedCoverageState && resultDisabledCss]}>
                                            <div css={[resultLabelCss, showBlockedCoverageState && resultLabelDisabledCss]}>{resultLabel}</div>
                                            {loadingQuote ? (
                                                <CircularProgress
                                                    size={32}
                                                    thickness={4}
                                                    style={{ color: '#ff7a45' }}
                                                />
                                            ) : (
                                                <div css={[resultValueCss, showBlockedCoverageState && resultValueDisabledCss]}>{pagoFormateado}</div>
                                            )}
                                        </div>
                                    )}

                                    {showSimulator && (
                                        <Button
                                            css={[ctaCss, showBlockedCoverageState && ctaDisabledCss]}
                                            endIcon={
                                                <ArrowForwardRoundedIcon style={{ color: showBlockedCoverageState ? '#d8d2cb' : '#ff7a45' }} />
                                            }
                                            onClick={handleContinue}
                                            disabled={!dedicacion || !monto || !plazo || !pago || loadingQuote}
                                        >
                                            {continueButtonText || 'Continuar solicitud'}
                                        </Button>
                                    )}

                                    {showSimulator && (
                                        <div css={footerHintCss}>
                                            {simulatorFooterHint || 'Sujeto a aprobación de crédito. Calculadora con fines ilustrativos.'}
                                        </div>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </section>

                <section css={benefitsSectionCss}>
                    <div css={benefitsInnerCss}>
                        <div css={benefitsEyebrowCss}>{benefitsEyebrow || 'Beneficios'}</div>

                        <Typography component="h2" css={benefitsTitleCss}>
                            {benefitsTitlePrefix || '¿Por qué'} <span css={italicAccentCss}>{benefitsTitleAccent || 'elegirnos?'}</span>
                        </Typography>

                        <Typography component="p" css={benefitsSubtitleCss}>
                            {benefitsSubtitleText || 'Hacemos que el proceso sea simple, transparente y diseñado para tu comodidad.'}
                        </Typography>

                        <Box
                            css={css`
                                display: none;

                                @media (min-width: 960px) {
                                    display: block;
                                }
                            `}
                        >
                            <Grid container spacing={3} style={{ marginTop: 18 }}>
                                <Grid item xs={12} md={4}>
                                    <Box css={benefitCardCss}>
                                        <span css={benefitIconWrapCss}>
                                            <DescriptionOutlinedIcon />
                                        </span>
                                        <Typography component="h3" css={benefitTitleCss}>
                                            {benefitOneTitle || 'Requisitos sencillos para solicitar'}
                                        </Typography>
                                        <Typography component="div" css={benefitTextCss}>
                                            {benefitOneLine1 || 'Identificación oficial vigente,'}
                                            <br />
                                            {benefitOneLine2 || 'Comprobante reciente de ingresos'}
                                            <br />
                                            {benefitOneLine3 || 'Comprobante reciente de domicilio.'}
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Box css={benefitCardCss}>
                                        <span css={benefitIconWrapCss}>
                                            <CreditCardOutlinedIcon />
                                        </span>
                                        <Typography component="h3" css={benefitTitleCss}>
                                            {benefitTwoTitle || 'Aplica en línea'}
                                        </Typography>
                                        <Typography component="div" css={benefitTextCss}>
                                            {benefitTwoLine1 || 'Obtén tu préstamo de forma presencial o, si cumples con los requisitos de elegibilidad, mediante depósito directo a tu tarjeta de débito, de manera rápida y sin complicaciones.'}
                                            <br />
                                            {/* {benefitTwoLine2 || 'sin filas, sin traslados y sin papeleo innecesario.'} */}
                                            {benefitTwoLine3 ? (
                                                <>
                                                    <br />
                                                    {benefitTwoLine3}
                                                </>
                                            ) : null}
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Box css={benefitCardCss}>
                                        <span css={benefitIconWrapCss}>
                                            <PhoneIphoneOutlinedIcon />
                                        </span>
                                        <Typography component="h3" css={benefitTitleCss}>
                                            {benefitThreeTitle || 'La comodidad para hacer tus pagos'}
                                        </Typography>
                                        <Typography component="div" css={benefitTextCss}>
                                            {benefitThreeLine1 || 'Acceso a tu cuenta las 24 horas del día desde'}
                                            &nbsp;
                                            {benefitThreeLine2 || 'donde te encuentres a través de nuestra APP'}
                                            &nbsp;
                                            {benefitThreeLine3 || 'Apoyo Financiero Mobile.'} <span css={benefitLinkCss}>{benefitThreeLinkText || 'Descárgala aquí.'}</span>
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>

                        <Box
                            css={css`
                                @media (min-width: 960px) {
                                    display: none;
                                }
                            `}
                        >
                            <div
                                css={mobileScrollerCss}
                                ref={benefitsScrollerRef}
                                onScroll={(event) =>
                                    updateActiveSlide(event.currentTarget, setActiveBenefitSlide, 3)
                                }
                            >
                                <div css={mobileSlideCss}>
                                    <Box css={benefitCardCss}>
                                        <span css={benefitIconWrapCss}>
                                            <DescriptionOutlinedIcon />
                                        </span>
                                        <Typography component="h3" css={benefitTitleCss}>
                                            {benefitOneTitle || 'Requisitos sencillos para solicitar'}
                                        </Typography>
                                        <Typography component="div" css={benefitTextCss}>
                                            1. {benefitOneLine1 || 'Identificación oficial vigente,'}
                                            <br />
                                            2. {benefitOneLine2 || 'Comprobante reciente de ingresos'}
                                            <br />
                                            3. {benefitOneLine3 || 'Comprobante reciente de domicilio.'}
                                        </Typography>
                                    </Box>
                                </div>

                                <div css={mobileSlideCss}>
                                    <Box css={benefitCardCss}>
                                        <span css={benefitIconWrapCss}>
                                            <CreditCardOutlinedIcon />
                                        </span>
                                        <Typography component="h3" css={benefitTitleCss}>
                                            {benefitTwoTitle || 'Aplica en línea'}
                                        </Typography>
                                        <Typography component="div" css={benefitTextCss}>
                                            {benefitTwoLine1 || 'Obtén tu préstamo de forma presencial o, si cumples con los requisitos de elegibilidad, mediante depósito directo a tu tarjeta de débito, de manera rápida y sin complicaciones.'}
                                            <br />
                                            {/* {benefitTwoLine2 || 'sin filas, sin traslados y sin papeleo innecesario.'} */}
                                            {benefitTwoLine3 ? (
                                                <>
                                                    <br />
                                                    {benefitTwoLine3}
                                                </>
                                            ) : null}
                                        </Typography>
                                    </Box>
                                </div>

                                <div css={mobileSlideCss}>
                                    <Box css={benefitCardCss}>
                                        <span css={benefitIconWrapCss}>
                                            <PhoneIphoneOutlinedIcon />
                                        </span>
                                        <Typography component="h3" css={benefitTitleCss}>
                                            {benefitThreeTitle || 'La comodidad para hacer tus pagos'}
                                        </Typography>
                                        <Typography component="div" css={benefitTextCss}>
                                            {benefitThreeLine1 || 'Acceso a tu cuenta las 24 horas del día desde'}
                                            <br />
                                            {benefitThreeLine2 || 'donde te encuentres a través de nuestra APP'}
                                            <br />
                                            {benefitThreeLine3 || 'Apoyo Financiero Mobile.'} <span css={benefitLinkCss}>{benefitThreeLinkText || 'Descárgala aquí.'}</span>
                                        </Typography>
                                    </Box>
                                </div>
                            </div>

                            <div css={mobileDotsCss}>
                                {[0, 1, 2].map((dotIndex) => (
                                    <span
                                        key={dotIndex}
                                        css={[mobileDotCss, activeBenefitSlide === dotIndex && mobileDotActiveCss]}
                                    />
                                ))}
                            </div>
                        </Box>
                    </div>
                </section>

                <section css={testimonialsSectionCss}>
                    <div css={benefitsInnerCss}>
                        <div css={testimonialsEyebrowCss}>{testimonialsEyebrowText || 'Testimonios'}</div>

                        <Typography component="h2" css={testimonialsTitleCss}>
                            {testimonialsTitlePrefix || 'Lo que dicen'} <span css={italicAccentCss}>{testimonialsTitleAccent || 'nuestros clientes'}</span>
                        </Typography>

                        <Typography component="p" css={testimonialsSubtitleCss}>
                            {testimonialsSubtitleText || 'Más de 50,000 personas ya han transformado su futuro financiero con nosotros.'}
                        </Typography>

                        <Box
                            css={css`
                                display: none;

                                @media (min-width: 960px) {
                                    display: block;
                                }
                            `}
                        >
                            <Grid container spacing={3} style={{ marginTop: 18 }}>
                                {testimonials.map((item) => (
                                    <Grid item xs={12} sm={6} lg={3} key={item.name}>
                                        <Box css={testimonialCardCss}>
                                            <div css={testimonialStarsCss}>{item.stars}</div>

                                            <Typography component="p" css={testimonialQuoteCss}>
                                                {item.quote}
                                            </Typography>

                                            <div css={testimonialAuthorCss}>
                                                <span css={[testimonialAvatarCss, css`background: ${item.avatarBg};`]}>
                                                    {item.initials}
                                                </span>
                                                <div>
                                                    <div css={testimonialNameCss}>{item.name}</div>
                                                    <div css={testimonialLocationCss}>{item.location}</div>
                                                </div>
                                            </div>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>

                        <Box
                            css={css`
                                @media (min-width: 960px) {
                                    display: none;
                                }
                            `}
                        >
                            <div
                                css={mobileScrollerCss}
                                ref={testimonialsScrollerRef}
                                onScroll={(event) =>
                                    updateActiveSlide(event.currentTarget, setActiveTestimonialSlide, testimonials.length)
                                }
                            >
                                {testimonials.map((item) => (
                                    <div css={mobileSlideCss} key={item.name}>
                                        <Box css={testimonialCardCss}>
                                            <div css={testimonialStarsCss}>{item.stars}</div>

                                            <Typography component="p" css={testimonialQuoteCss}>
                                                {item.quote}
                                            </Typography>

                                            <div css={testimonialAuthorCss}>
                                                <span css={[testimonialAvatarCss, css`background: ${item.avatarBg};`]}>
                                                    {item.initials}
                                                </span>
                                                <div>
                                                    <div css={testimonialNameCss}>{item.name}</div>
                                                    <div css={testimonialLocationCss}>{item.location}</div>
                                                </div>
                                            </div>
                                        </Box>
                                    </div>
                                ))}
                            </div>

                            <div css={mobileDotsCss}>
                                {testimonials.map((item, dotIndex) => (
                                    <span
                                        key={item.name}
                                        css={[mobileDotCss, activeTestimonialSlide === dotIndex && mobileDotActiveCss]}
                                    />
                                ))}
                            </div>
                        </Box>
                    </div>
                </section>
            </Container>

            <ModalInfo
                autoClose={false}
                icon={CancelIcon}
                title={modalTitleSorry || 'Lo sentimos'}
                color="warning"
                open={openProductModal}
                body={<div><p>{modalBodyAge || 'No tienes la antigüedad mínima para acceder a un crédito. Gracias.'}</p></div>}
                btnRight="TERMINAR"
                onChange={() => {
                    setOpenProductModal(false)
                    navigate(pageContext.errorPath)
                }}
            />
        </div>
    )
}

export default memo(withThemeProps(Simulador, 'VDNPSimulador'))
